import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ChevronLeft, ChevronRight, Plus, Trash2, Clock, Calendar as CalendarIcon, Bell, ArrowLeft, UserCircle, Ban, Palmtree } from 'lucide-react';
import { toast } from 'sonner';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, getCustomers, getAllEmployees, adminCreateVacation } from '../lib/api';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAYS_FULL = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const COLOR_OPTIONS = [
    { value: '#16a34a', label: 'Grün' },
    { value: '#2563eb', label: 'Blau' },
    { value: '#dc2626', label: 'Rot' },
    { value: '#ca8a04', label: 'Gelb' },
    { value: '#9333ea', label: 'Lila' },
    { value: '#ea580c', label: 'Orange' },
    { value: '#64748b', label: 'Grau' },
];

const REMINDER_OPTIONS = [
    { value: 'none', label: 'Keine Erinnerung', minutes: null },
    { value: '0', label: 'Zum Zeitpunkt', minutes: 0 },
    { value: '15', label: '15 Minuten vorher', minutes: 15 },
    { value: '30', label: '30 Minuten vorher', minutes: 30 },
    { value: '60', label: '1 Stunde vorher', minutes: 60 },
    { value: '1440', label: '1 Tag vorher', minutes: 1440 },
];

const EVENT_TYPES = [
    { value: 'event', label: 'Termin', icon: CalendarIcon },
    { value: 'appointment', label: 'Kundentermin', icon: UserCircle },
    { value: 'vacation', label: 'Mitarbeiter-Urlaub', icon: Palmtree },
    { value: 'blocking', label: 'Blockierung', icon: Ban },
];

const BLOCKING_TYPES = [
    { value: 'vacation', label: 'Urlaub', color: '#ef4444' },
    { value: 'office', label: 'Büroarbeit', color: '#6366f1' },
    { value: 'sick', label: 'Krankheit', color: '#f59e0b' },
    { value: 'training', label: 'Schulung', color: '#8b5cf6' },
    { value: 'other', label: 'Sonstiges', color: '#64748b' },
];

const VACATION_TYPES = [
    { value: 'VACATION', label: 'Urlaub', color: '#ef4444' },
    { value: 'SICK', label: 'Krankheit', color: '#f59e0b' },
    { value: 'OTHER', label: 'Sonstiges (bezahlt)', color: '#64748b' },
];

const AdminCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [viewEventDialog, setViewEventDialog] = useState(false);
    const [viewMode, setViewMode] = useState('month');
    const [selectedDayDate, setSelectedDayDate] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [employees, setEmployees] = useState([]);
    
    const [form, setForm] = useState({
        title: '',
        date: '',
        end_date: '',  // For vacation date range
        start_time: '',
        end_time: '',
        color: '#16a34a',
        description: '',
        all_day: true,
        reminder_minutes: null,
        event_type: 'event',
        customer_id: '',
        employee_id: '',
        blocking_type: '',
        vacation_type: 'VACATION'  // For employee vacation
    });

    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    useEffect(() => {
        loadEvents();
        // Customers and employees are loaded only when needed (when dialog opens)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMonth, currentYear]);

    // Lazy load customers and employees when dialog opens
    useEffect(() => {
        if (dialogOpen) {
            loadCustomersAndEmployees();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogOpen]);

    const loadCustomersAndEmployees = async () => {
        // Only load if not already loaded
        if (customers.length > 0 && employees.length > 0) return;
        try {
            const [customersRes, employeesRes] = await Promise.all([
                getCustomers(),
                getAllEmployees()  // Alle Mitarbeiter laden (auch ohne Smartphone) für Urlaub/Blockierung
            ]);
            setCustomers(customersRes.data || []);
            setEmployees(employeesRes.data || []);
        } catch (err) {
            // Silent error
        }
    };

    const loadEvents = async () => {
        try {
            const res = await getCalendarEvents(currentMonth + 1, currentYear);
            setEvents(res.data);
        } catch (err) {
            // Silent error
        } finally {
            setLoading(false);
        }
    };

    const goToPrevMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const getCalendarDays = () => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const daysInMonth = lastDay.getDate();
        
        let startDay = firstDay.getDay() - 1;
        if (startDay < 0) startDay = 6;
        
        const days = [];
        
        const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
        for (let i = startDay - 1; i >= 0; i--) {
            days.push({
                day: prevMonthLastDay - i,
                isCurrentMonth: false,
                date: new Date(currentYear, currentMonth - 1, prevMonthLastDay - i)
            });
        }
        
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                day: i,
                isCurrentMonth: true,
                date: new Date(currentYear, currentMonth, i)
            });
        }
        
        const remainingDays = 42 - days.length;
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                day: i,
                isCurrentMonth: false,
                date: new Date(currentYear, currentMonth + 1, i)
            });
        }
        
        return days;
    };

    const formatDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getEventsForDate = (date) => {
        const dateStr = formatDateString(date);
        const dayEvents = events.filter(e => e.date === dateStr);
        
        // Sort: vacation events first, then by time
        return dayEvents.sort((a, b) => {
            // Vacation events (Mitarbeiterurlaub) always first
            const aIsVacation = a.event_type === 'vacation';
            const bIsVacation = b.event_type === 'vacation';
            
            if (aIsVacation && !bIsVacation) return -1;
            if (!aIsVacation && bIsVacation) return 1;
            
            // Then sort by start_time if available
            if (a.start_time && b.start_time) {
                return a.start_time.localeCompare(b.start_time);
            }
            
            return 0;
        });
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() && 
               date.getMonth() === today.getMonth() && 
               date.getFullYear() === today.getFullYear();
    };

    const getWeekdayName = (date) => {
        const dayIndex = date.getDay();
        const germanIndex = dayIndex === 0 ? 6 : dayIndex - 1;
        return WEEKDAYS_FULL[germanIndex];
    };

    const handleDayClick = (day) => {
        setSelectedDayDate(day.date);
        setViewMode('day');
    };

    const handleAddEventOnDay = (dateStr) => {
        setSelectedDate(dateStr);
        setForm({
            ...form,
            date: dateStr,
            end_date: dateStr,
            title: '',
            description: '',
            start_time: '',
            end_time: '',
            color: '#16a34a',
            all_day: true,
            reminder_minutes: null,
            event_type: 'event',
            customer_id: '',
            employee_id: '',
            blocking_type: '',
            vacation_type: 'VACATION'
        });
        setSelectedEvent(null);
        setDialogOpen(true);
    };

    const handleEventClick = (event, e, day) => {
        e.stopPropagation();
        // Go to day view to see full event details
        setSelectedDayDate(day.date);
        setViewMode('day');
    };

    const handleEditEvent = () => {
        setForm({
            title: selectedEvent.title,
            date: selectedEvent.date,
            start_time: selectedEvent.start_time || '',
            end_time: selectedEvent.end_time || '',
            color: selectedEvent.color || '#16a34a',
            description: selectedEvent.description || '',
            all_day: selectedEvent.all_day !== false,
            reminder_minutes: selectedEvent.reminder_minutes,
            event_type: selectedEvent.event_type || 'event',
            customer_id: selectedEvent.customer_id || '',
            employee_id: selectedEvent.employee_id || '',
            blocking_type: selectedEvent.blocking_type || ''
        });
        setViewEventDialog(false);
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        // Validation based on event type
        if (form.event_type === 'vacation') {
            // Vacation requires employee and dates
            if (!form.employee_id) {
                toast.error('Bitte wählen Sie einen Mitarbeiter');
                return;
            }
            if (!form.date) {
                toast.error('Bitte wählen Sie ein Startdatum');
                return;
            }
            if (!form.end_date) {
                toast.error('Bitte wählen Sie ein Enddatum');
                return;
            }
            if (form.end_date < form.date) {
                toast.error('Enddatum muss nach dem Startdatum liegen');
                return;
            }
        } else if (form.event_type === 'blocking') {
            // Blocking requires employee
            if (!form.employee_id) {
                toast.error('Bitte wählen Sie einen Mitarbeiter für die Blockierung');
                return;
            }
            if (!form.date) {
                toast.error('Bitte wählen Sie ein Datum');
                return;
            }
        } else {
            // Regular events and appointments need title and date
            if (!form.title.trim()) {
                toast.error('Bitte geben Sie einen Titel ein');
                return;
            }
            if (!form.date) {
                toast.error('Bitte wählen Sie ein Datum');
                return;
            }
        }

        try {
            // Handle vacation type separately - uses different API
            if (form.event_type === 'vacation') {
                const vacationData = {
                    user_id: form.employee_id,
                    start_date: form.date,
                    end_date: form.end_date,
                    vacation_type: form.vacation_type,
                    notes: form.description || null
                };
                await adminCreateVacation(vacationData);
                
                const employee = employees.find(e => e.id === form.employee_id);
                const typeLabel = VACATION_TYPES.find(t => t.value === form.vacation_type)?.label || 'Urlaub';
                toast.success(`${typeLabel} für ${employee?.name || 'Mitarbeiter'} eingetragen`);
                
                setDialogOpen(false);
                setSelectedEvent(null);
                
                // Navigate to start date month
                const eventDate = new Date(form.date);
                const eventMonth = eventDate.getMonth();
                const eventYear = eventDate.getFullYear();
                if (eventMonth !== currentMonth || eventYear !== currentYear) {
                    setCurrentDate(new Date(eventYear, eventMonth, 1));
                } else {
                    loadEvents();
                }
                return;
            }
            
            // For blockings, ensure title is set from blocking type
            let submitData = { ...form };
            if (form.event_type === 'blocking' && !submitData.title) {
                const blockType = BLOCKING_TYPES.find(b => b.value === form.blocking_type);
                submitData.title = blockType?.label || 'Blockierung';
            }

            if (selectedEvent) {
                await updateCalendarEvent(selectedEvent.id, submitData);
                toast.success('Eintrag aktualisiert');
            } else {
                await createCalendarEvent(submitData);
                const typeLabel = form.event_type === 'blocking' ? 'Blockierung' : 
                                  form.event_type === 'appointment' ? 'Kundentermin' : 'Termin';
                toast.success(`${typeLabel} erstellt`);
            }
            setDialogOpen(false);
            setSelectedEvent(null);
            
            // Navigate to the month of the created/updated event
            if (form.date) {
                const eventDate = new Date(form.date);
                const eventMonth = eventDate.getMonth();
                const eventYear = eventDate.getFullYear();
                
                // Only navigate if the event is in a different month
                if (eventMonth !== currentMonth || eventYear !== currentYear) {
                    setCurrentDate(new Date(eventYear, eventMonth, 1));
                } else {
                    loadEvents();
                }
            } else {
                loadEvents();
            }
        } catch (err) {
            // Silent error
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDelete = async () => {
        if (!selectedEvent) return;
        if (!window.confirm('Termin wirklich löschen?')) return;

        try {
            await deleteCalendarEvent(selectedEvent.id);
            toast.success('Termin gelöscht');
            setViewEventDialog(false);
            setSelectedEvent(null);
            loadEvents();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    const calendarDays = getCalendarDays();

    // Render Day View
    const renderDayView = () => {
        if (!selectedDayDate) return null;
        const dateStr = formatDateString(selectedDayDate);
        const dayEvents = getEventsForDate(selectedDayDate);
        const weekdayName = getWeekdayName(selectedDayDate);
        
        return (
            <>
                <div className="flex items-center justify-between mb-4">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setViewMode('month')}
                        className="flex items-center gap-1"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Zurück
                    </Button>
                    <div className="text-center">
                        <p className="text-lg font-semibold">
                            {weekdayName}, {selectedDayDate.getDate()}. {MONTHS[selectedDayDate.getMonth()]}
                        </p>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleAddEventOnDay(dateStr)}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Neu
                    </Button>
                </div>

                <div className="space-y-2">
                    {dayEvents.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
                            <p>Keine Termine an diesem Tag</p>
                        </div>
                    ) : (
                        dayEvents.map(event => {
                            // Split title into customer name (line 1) and task (line 2)
                            const titleParts = event.title?.split('\n') || [event.title];
                            const customerName = titleParts[0] || '';
                            const taskName = titleParts[1] || '';
                            const isCompleted = event.completed === true;
                            const isBlocking = event.event_type === 'blocking';
                            const isAppointment = event.event_type === 'appointment';
                            
                            return (
                                <div
                                    key={event.id}
                                    onClick={() => {
                                        setSelectedEvent(event);
                                        setViewEventDialog(true);
                                    }}
                                    className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                                        isBlocking ? 'bg-muted/30' : ''
                                    }`}
                                    style={{ borderLeftWidth: '4px', borderLeftColor: event.color || '#16a34a' }}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                {isBlocking && <Ban className="h-4 w-4 text-muted-foreground" />}
                                                {isAppointment && <UserCircle className="h-4 w-4 text-blue-500" />}
                                                <h4 className="font-semibold text-sm">{customerName}</h4>
                                            </div>
                                            {taskName && (
                                                <p className="text-sm text-muted-foreground">{taskName}</p>
                                            )}
                                            {event.description && (
                                                <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line line-clamp-2">
                                                    {event.description}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 ml-2">
                                            {isBlocking ? (
                                                <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded">
                                                    blockiert
                                                </span>
                                            ) : isCompleted ? (
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                                                    {event.start_time && event.end_time ? (
                                                        <>
                                                            <Clock className="h-3 w-3" />
                                                            {event.start_time} - {event.end_time}
                                                        </>
                                                    ) : (
                                                        'erledigt'
                                                    )}
                                                </span>
                                            ) : !event.all_day && event.start_time ? (
                                                <span className="text-xs bg-muted px-2 py-0.5 rounded flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {event.start_time}
                                                    {event.end_time && ` - ${event.end_time}`}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </>
        );
    };

    // Render Month View
    const renderMonthView = () => (
        <>
            <div className="grid grid-cols-7 gap-px mb-1">
                {WEEKDAYS.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                        {day}
                    </div>
                ))}
            </div>
            
            <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {calendarDays.map((day) => {
                    const dayEvents = getEventsForDate(day.date);
                    const today = isToday(day.date);
                    
                    return (
                        <div
                            key={day.date.toString()}
                            onClick={() => handleDayClick(day)}
                            className={`
                                min-h-[70px] p-1 bg-background cursor-pointer transition-colors hover:bg-muted/50
                                ${!day.isCurrentMonth ? 'opacity-40' : ''}
                            `}
                        >
                            <div className={`
                                text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full
                                ${today ? 'bg-primary text-primary-foreground' : ''}
                            `}>
                                {day.day}
                            </div>
                            <div className="space-y-0.5">
                                {dayEvents.slice(0, 2).map(event => (
                                    <div
                                        key={event.id}
                                        onClick={(e) => handleEventClick(event, e, day)}
                                        className="text-[10px] px-1 py-0.5 rounded truncate text-white cursor-pointer hover:opacity-80"
                                        style={{ backgroundColor: event.color || '#16a34a' }}
                                        title={event.title}
                                    >
                                        {event.title}
                                    </div>
                                ))}
                                {dayEvents.length > 2 && (
                                    <div className="text-[10px] text-muted-foreground px-1">
                                        +{dayEvents.length - 2} mehr
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                onClick={() => {
                    const today = formatDateString(new Date());
                    setSelectedDate(today);
                    setForm({ ...form, date: today, title: '', description: '' });
                    setSelectedEvent(null);
                    setDialogOpen(true);
                }}
            >
                <Plus className="h-4 w-4 mr-1" />
                Neuer Termin
            </Button>
        </>
    );

    return (
        <Card className="h-full">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-primary" />
                        Kalender
                    </CardTitle>
                    {viewMode === 'month' && (
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={goToToday} 
                                className="text-xs font-medium bg-primary/10 hover:bg-primary/20 border-primary/30 text-primary"
                                data-testid="calendar-today-btn"
                            >
                                Heute
                            </Button>
                            <div className="flex items-center gap-0.5">
                                <Button variant="ghost" size="sm" onClick={goToPrevMonth} data-testid="calendar-prev-month">
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={goToNextMonth} data-testid="calendar-next-month">
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
                {viewMode === 'month' && (
                    <p className="text-sm font-medium text-center mt-1">
                        {MONTHS[currentMonth]} {currentYear}
                    </p>
                )}
            </CardHeader>
            <CardContent className="p-2">
                {viewMode === 'month' ? renderMonthView() : renderDayView()}
            </CardContent>

            {/* Create/Edit Event Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedEvent ? 'Termin bearbeiten' : 'Neuer Eintrag'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Event Type Selection */}
                        <div>
                            <Label className="mb-2 block">Art des Eintrags</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {EVENT_TYPES.map(type => {
                                    const Icon = type.icon;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => {
                                                // Auto-set color based on type
                                                let defaultColor = form.color;
                                                if (type.value === 'blocking') {
                                                    defaultColor = '#64748b';
                                                } else if (type.value === 'appointment') {
                                                    defaultColor = '#2563eb';
                                                }
                                                setForm({
                                                    ...form, 
                                                    event_type: type.value,
                                                    color: defaultColor,
                                                    blocking_type: type.value === 'blocking' ? 'other' : ''
                                                });
                                            }}
                                            className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                                                form.event_type === type.value 
                                                    ? 'border-primary bg-primary/10' 
                                                    : 'border-muted hover:border-muted-foreground/30'
                                            }`}
                                            data-testid={`event-type-${type.value}`}
                                        >
                                            <Icon className={`h-5 w-5 mb-1 ${form.event_type === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                                            <span className="text-xs font-medium">{type.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Blocking Type (only for blockings) */}
                        {form.event_type === 'blocking' && (
                            <div>
                                <Label>Blockierungsgrund</Label>
                                <Select 
                                    value={form.blocking_type || 'other'} 
                                    onValueChange={(v) => {
                                        const blockType = BLOCKING_TYPES.find(b => b.value === v);
                                        setForm({
                                            ...form, 
                                            blocking_type: v,
                                            color: blockType?.color || '#64748b',
                                            title: blockType?.label || form.title
                                        });
                                    }}
                                >
                                    <SelectTrigger className="mt-1" data-testid="blocking-type">
                                        <SelectValue placeholder="Grund wählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {BLOCKING_TYPES.map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                                                    {type.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Employee Selection (for appointments and blockings) */}
                        {(form.event_type === 'appointment' || form.event_type === 'blocking') && (
                            <div>
                                <Label>Mitarbeiter zuweisen {form.event_type === 'blocking' ? '*' : ''}</Label>
                                <Select 
                                    value={form.employee_id || ''} 
                                    onValueChange={(v) => setForm({...form, employee_id: v})}
                                >
                                    <SelectTrigger className="mt-1" data-testid="event-employee">
                                        <SelectValue placeholder="Mitarbeiter wählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map(emp => (
                                            <SelectItem key={emp.id} value={emp.id}>
                                                {emp.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Vacation Fields */}
                        {form.event_type === 'vacation' && (
                            <>
                                <div>
                                    <Label>Mitarbeiter *</Label>
                                    <Select 
                                        value={form.employee_id || ''} 
                                        onValueChange={(v) => setForm({...form, employee_id: v})}
                                    >
                                        <SelectTrigger className="mt-1" data-testid="vacation-employee">
                                            <SelectValue placeholder="Mitarbeiter wählen" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {employees.map(emp => (
                                                <SelectItem key={emp.id} value={emp.id}>
                                                    {emp.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Art *</Label>
                                    <Select 
                                        value={form.vacation_type} 
                                        onValueChange={(v) => {
                                            const vacType = VACATION_TYPES.find(t => t.value === v);
                                            setForm({...form, vacation_type: v, color: vacType?.color || '#10b981'});
                                        }}
                                    >
                                        <SelectTrigger className="mt-1" data-testid="vacation-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VACATION_TYPES.map(type => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                                                        {type.label}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Von *</Label>
                                        <Input
                                            type="date"
                                            value={form.date}
                                            onChange={(e) => setForm({...form, date: e.target.value})}
                                            data-testid="vacation-start-date"
                                        />
                                    </div>
                                    <div>
                                        <Label>Bis *</Label>
                                        <Input
                                            type="date"
                                            value={form.end_date}
                                            onChange={(e) => setForm({...form, end_date: e.target.value})}
                                            min={form.date}
                                            data-testid="vacation-end-date"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Notiz (optional)</Label>
                                    <Textarea
                                        value={form.description}
                                        onChange={(e) => setForm({...form, description: e.target.value})}
                                        placeholder="z.B. Sommerurlaub, Arztbesuch..."
                                        rows={2}
                                        data-testid="vacation-notes"
                                    />
                                </div>
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                                    <strong>Hinweis:</strong> Der Urlaub wird direkt genehmigt und im Kalender angezeigt. Er wird <strong>nicht</strong> in der Zeiterfassung berücksichtigt.
                                </div>
                            </>
                        )}

                        {/* Customer Selection (only for appointments) */}
                        {form.event_type === 'appointment' && (
                            <div>
                                <Label>Kunde</Label>
                                <Select 
                                    value={form.customer_id || 'none'} 
                                    onValueChange={(v) => {
                                        const actualValue = v === 'none' ? '' : v;
                                        const customer = customers.find(c => c.id === actualValue);
                                        setForm({
                                            ...form, 
                                            customer_id: actualValue,
                                            title: customer ? `${customer.name}${form.title && !form.title.startsWith(customer.name) ? '\n' + form.title : ''}` : form.title
                                        });
                                    }}
                                >
                                    <SelectTrigger className="mt-1" data-testid="event-customer">
                                        <SelectValue placeholder="Kunde wählen (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Kein Kunde</SelectItem>
                                        {customers.map(cust => (
                                            <SelectItem key={cust.id} value={cust.id}>
                                                {cust.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Title - auto-filled for blockings, editable for others, hidden for vacation */}
                        {form.event_type !== 'vacation' && (
                            <div>
                                <Label>Titel {form.event_type !== 'blocking' ? '*' : ''}</Label>
                                <Input
                                    value={form.title}
                                    onChange={(e) => setForm({...form, title: e.target.value})}
                                    placeholder={form.event_type === 'appointment' ? 'z.B. Besprechung' : form.event_type === 'blocking' ? 'Wird automatisch gesetzt' : 'Titel eingeben'}
                                    data-testid="event-title"
                                    disabled={form.event_type === 'blocking'}
                                />
                            </div>
                        )}

                        {/* Date - hidden for vacation (has its own date fields) */}
                        {form.event_type !== 'vacation' && (
                            <div>
                                <Label>Datum *</Label>
                                <Input
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => setForm({...form, date: e.target.value})}
                                    data-testid="event-date"
                                />
                            </div>
                        )}

                        {/* All-day toggle - hidden for vacation */}
                        {form.event_type !== 'vacation' && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="all-day"
                                    checked={form.all_day}
                                    onChange={(e) => setForm({...form, all_day: e.target.checked})}
                                    className="rounded"
                                />
                                <Label htmlFor="all-day" className="cursor-pointer">Ganztägig</Label>
                            </div>
                        )}

                        {!form.all_day && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Startzeit</Label>
                                    <Input
                                        type="time"
                                        value={form.start_time}
                                        onChange={(e) => setForm({...form, start_time: e.target.value})}
                                        data-testid="event-start-time"
                                    />
                                </div>
                                <div>
                                    <Label>Endzeit</Label>
                                    <Input
                                        type="time"
                                        value={form.end_time}
                                        onChange={(e) => setForm({...form, end_time: e.target.value})}
                                        data-testid="event-end-time"
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <Label className="flex items-center gap-2">
                                <Bell className="h-4 w-4" />
                                Erinnerung
                            </Label>
                            <Select 
                                value={form.reminder_minutes?.toString() || 'none'} 
                                onValueChange={(v) => setForm({
                                    ...form, 
                                    reminder_minutes: v === 'none' ? null : parseInt(v)
                                })}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Erinnerung wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {REMINDER_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Color selection - only for non-blocking events */}
                        {form.event_type !== 'blocking' && (
                            <div>
                                <Label>Farbe</Label>
                                <div className="flex gap-2 mt-1">
                                    {COLOR_OPTIONS.map(color => (
                                        <button
                                            key={color.value}
                                            type="button"
                                            onClick={() => setForm({...form, color: color.value})}
                                            className={`w-8 h-8 rounded-full transition-all ${
                                                form.color === color.value ? 'ring-2 ring-offset-2 ring-primary' : ''
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            title={color.label}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <Label>Beschreibung</Label>
                            <Textarea
                                value={form.description}
                                onChange={(e) => setForm({...form, description: e.target.value})}
                                placeholder="Weitere Details..."
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSubmit} data-testid="event-submit">
                            {selectedEvent ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Event Dialog */}
            <Dialog open={viewEventDialog} onOpenChange={setViewEventDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: selectedEvent?.color || '#16a34a' }}
                            />
                            {selectedEvent?.title}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-4">
                        <div className="flex items-center gap-2 text-sm">
                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                            <span>
                                {selectedEvent?.date && new Date(selectedEvent.date).toLocaleDateString('de-DE', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </span>
                        </div>
                        {!selectedEvent?.all_day && selectedEvent?.start_time && (
                            <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span>
                                    {selectedEvent.start_time}
                                    {selectedEvent.end_time && ` - ${selectedEvent.end_time}`}
                                </span>
                            </div>
                        )}
                        {selectedEvent?.description && (
                            <div className="text-sm text-muted-foreground mt-2 p-3 bg-muted/50 rounded whitespace-pre-line">
                                {selectedEvent.description}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex justify-between">
                        <Button variant="destructive" size="sm" onClick={handleDelete}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Löschen
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setViewEventDialog(false)}>
                                Schließen
                            </Button>
                            <Button onClick={handleEditEvent}>
                                Bearbeiten
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default AdminCalendar;
